import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { dashboardService } from '@/api/services/dashboardService'
import { schoolService } from '@/api/services/schoolService'
import { useAuthStore } from '@/store/useAuthStore'

export function useDashboardStats(p?: { start_date?: string; end_date?: string; city_id?: number }) {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['dashboard', 'analytics', 'admin', p],
    queryFn:  () => dashboardService.adminAnalytics(p),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    enabled:   user?.user_type === 'admin',
  })
}

export function useSchoolDashboardStats(p?: Record<string, unknown>) {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['dashboard', 'analytics', 'school', p],
    queryFn:  () => dashboardService.schoolAnalytics(p as any),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    enabled:   user?.user_type === 'school_admin',
  })
}

export function useAdminRevenue(p?: { year?: number; city_id?: number; start_date?: string; end_date?: string }) {
  return useQuery({
    queryKey: ['dashboard', 'revenue', 'admin', p],
    queryFn:  () => dashboardService.adminRevenue(p),
    staleTime: 120_000,
  })
}

export function usePendingVerifications(p?: { page?: number; limit?: number; sort_by?: string; is_verified?: number }) {
  const user = useAuthStore(s => s.user)
  const isSchoolAdmin = (user as any)?.user_type === 'school_admin'
  return useQuery({
    queryKey: ['dashboard', 'pending-verifications', p, isSchoolAdmin],
    queryFn:  () => dashboardService.pendingVerifications({ ...p, isSchoolAdmin }),
    staleTime: 30_000,
  })
}

// ── Heat Map data hooks ──────────────────────────────────────────────────────
// `radius` is accepted for the query key only — both trainers and students always
// send radius: 1000 (hardcoded in dashboardService), per request.
export function useNearestTrainers(center: { lat: number; lng: number }, radius = 500, enabled = true) {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['dashboard', 'heatmap', 'trainers', center, radius],
    queryFn:  () => dashboardService.nearestTrainers({ latitude: center.lat, longitude: center.lng }),
    staleTime: 60_000,
    enabled:   enabled && user?.user_type === 'admin',
  })
}

// School admins map their OWN trainers only: get-school-trainer-list is scoped
// server-side to the caller's school via the auth token.
export function useSchoolTrainersForMap(enabled = true) {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['dashboard', 'heatmap', 'school-trainers'],
    queryFn:  () => schoolService.schoolTrainers({ page: 1, limit: 500 }),
    staleTime: 60_000,
    enabled:   enabled && user?.user_type === 'school_admin',
  })
}

export function useStudentsUnderRadius(center: { lat: number; lng: number }, radius = 500, enabled = true) {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['dashboard', 'heatmap', 'students', center, radius],
    queryFn:  () => dashboardService.studentsUnderRadius({ latitude: center.lat, longitude: center.lng }),
    staleTime: 60_000,
    enabled:   enabled && user?.user_type === 'admin',
  })
}

// Keep backward compat alias
export const useBookingTrend = useAdminRevenue
